import { ReactNode, useCallback, useRef, useState } from 'react'

import PlainModal, {
  PlainModalContent,
  PlainModalFooter,
  PlainModalHeader,
} from 'app/common/ui-next/plain-modal'

import { OrgId } from 'app/dataTypes/org'
import usePopup from 'app/common/hooks/usePopup'
import ControlsBar from 'app/common/ui-next/controls-bar'
import PlainButton from 'app/common/ui-next/plain-button'
import PlainIcon, { colorTypes, iconTypes } from 'app/common/ui-next/plain-icon'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link'

import styles from './SortKitModal.module.scss'
import { CopySortKit } from './CopySortKit'
import { useSortKitsSubmit } from './useSortKitsSubmit'
import { useCopySortKitsSubmit } from './useCopySortKitsSubmit'
import { SortKit } from './SortKit'
import { Complexes } from '../useKits'

type Props = {
  orgId: OrgId
  kits: Complexes
  header: string
  onSuccessSubmit: () => void
  children: (toggle: () => void) => ReactNode
}

export const SortKitModal = ({ orgId, kits, header, onSuccessSubmit, children }: Props) => {
  const [displayed, setDisplayed] = useState(false)
  const [copySort, setCopySort] = useState<boolean>(false)
  const sortUpdated = useRef<boolean>(false)

  const popup = usePopup()

  const handleCopySort = useCallback(() => {
    setCopySort(true)
  }, [])
  const handleCloseCopySort = useCallback(() => {
    setCopySort(false)
  }, [])

  const [{ kitList, modified, pending }, { submit, resetSort, moveItem }] = useSortKitsSubmit({
    orgId,
    kits,
  })
  const [{ pending: copySortPending }, { submit: copySortSubmit, onSelectedOrgs }] =
    useCopySortKitsSubmit({ orgId, onSuccess: handleCloseCopySort })

  const toggle = useCallback(() => {
    setDisplayed((state) => !state)
    if (sortUpdated.current) {
      onSuccessSubmit()
    }
    resetSort()
  }, [onSuccessSubmit, resetSort])

  const handleCancel = useCallback(() => {
    if (copySort) {
      handleCloseCopySort()
    } else {
      resetSort()
    }
  }, [copySort, handleCloseCopySort, resetSort])

  const handleSubmit = useCallback(async () => {
    if (copySort) {
      await copySortSubmit()
      popup('Сортировка успешно скопирована')
      handleCloseCopySort()
    } else {
      const res = await submit()
      sortUpdated.current = res
    }
  }, [copySort, copySortSubmit, submit, popup, handleCloseCopySort])

  return (
    <>
      {children(toggle)}
      {displayed && (
        <PlainModal formId="copy_kits" className={styles.root} show>
          <PlainModalHeader onCrossClick={toggle}>
            {copySort ? 'Копировать сортировку' : header}
          </PlainModalHeader>

          <PlainModalContent className={styles.content} unscrollable>
            {copySort ? (
              <CopySortKit
                orgId={orgId}
                disabled={copySortPending}
                onSelectedOrgs={onSelectedOrgs}
              />
            ) : (
              <SortKit kitList={kitList} disabled={pending} moveItem={moveItem} />
            )}
          </PlainModalContent>

          <PlainModalFooter>
            <ControlsBar right>
              {(modified || copySort) && (
                <PlainButton onClick={handleCancel} outline disabled={pending}>
                  {'Отменить'}
                </PlainButton>
              )}

              {modified || copySort ? (
                <PlainButton onClick={handleSubmit} disabled={pending || copySortPending}>
                  {pending || copySortPending
                    ? copySort
                      ? 'Копируется...'
                      : 'Сохраняется...'
                    : copySort
                      ? 'Копировать'
                      : 'Сохранить'}
                </PlainButton>
              ) : (
                <PlainButtonAsLink onClick={handleCopySort} linkClassName={styles.copySetting}>
                  <PlainIcon
                    width="25px"
                    height="auto"
                    type={iconTypes.COPY_SETTING}
                    colorType={colorTypes.COLOR_PRIMARY}
                  />
                  <span>копировать сортировку</span>
                </PlainButtonAsLink>
              )}
            </ControlsBar>
          </PlainModalFooter>
        </PlainModal>
      )}
    </>
  )
}
